import { type Module } from "vuex";
import type {
  IntegrationSyncStatusResponse,
  CallTestRequest,
  DefaultApi,
  PositionConversationFlowMetricsRequest,
  PositionConversationFlowMetrics,
  PositionFaq,
  PositionFaqBulkRequest,
  PositionQualificationCheckResponse,
  PositionQualificationQuestionGenerationRequest,
  PositionQualificationQuestionGenerationResponse,
  LivePositionResponse,
  PositionListItemResponse,
  PositionPatchRequest,
  PositionRequest,
  SimulationReportRecipients,
  SimulationSettingCoverageResult,
} from "@/api";
import { CallSimulationJob } from "@/models/call_simulation_job";
import {
  buildPositionCatalog,
  type FaqCatalogResponse,
} from "@/models/faq_catalog";
import {
  buildMissingJobDetailsDefaultsPatch,
  LivePosition,
  Position,
} from "@/models/position";
import { PositionSyncStatus } from "@/models/position_sync_status";
import { LivePositions, Positions } from "@/models/positions";
import type { State as RootState } from "@/stores";
export interface State {
  positions?: Positions;
  positionsLoaded: boolean;
  positionSyncStatus?: PositionSyncStatus;
  positionSyncStatusLoaded: boolean;
  atsSyncing: boolean;
  atsSyncProgress: number;
  atsSyncFailures: number;
  atsRefreshRequested: boolean;
}

interface GetPositionsOptions {
  refresh?: boolean;
}

const positionsRequests = new WeakMap<State, Promise<Positions>>();
let atsRefreshPromise: Promise<number> | null = null;

/**
 * Serializes patchPosition calls per position id: a later PATCH for the same id starts only
 * after the earlier one has resolved and committed, so two components autosaving concurrent
 * fields can never have an older PATCH response overwrite a newer one in the store.
 * Module-scoped (not Vuex state) since it holds promises, not data.
 */
export const patchPositionChains = new Map<number, Promise<unknown>>();
const pendingPositionSyncStatus = new WeakMap<
  State,
  Promise<PositionSyncStatus>
>();

export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    positions: undefined,
    positionsLoaded: false,
    positionSyncStatus: undefined,
    positionSyncStatusLoaded: false,
    atsSyncing: false,
    atsSyncProgress: 0,
    atsSyncFailures: 0,
    atsRefreshRequested: false,
  },
  actions: {
    // The candidate FAQ catalog is static, client-owned content (see models/faq_catalog.ts).
    // Kept as an action so callers stay decoupled from where it comes from, and not cached in
    // state: the consumer owns the per-position catalog with its own stale/refetch guard.
    getFaqQuestionCatalog(): Promise<FaqCatalogResponse> {
      return Promise.resolve(buildPositionCatalog());
    },
    async getPositionFaqs(
      _context,
      positionId: number,
    ): Promise<PositionFaq[]> {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      return (await api.getPositionFaqs(positionId)).data;
    },
    async putPositionFaqs(
      _context,
      payload: {
        positionId: number;
        body: PositionFaqBulkRequest;
      },
    ): Promise<PositionFaq[]> {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      return (await api.putPositionFaqs(payload.positionId, payload.body)).data;
    },
    async getQualificationCheck(
      _context,
      positionId: number,
    ): Promise<PositionQualificationCheckResponse> {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      return (await api.getPositionQualificationCheck(positionId)).data;
    },
    async getPositions(
      { state, commit },
      { refresh = false }: GetPositionsOptions = {},
    ) {
      const currentRequest = positionsRequests.get(state);
      if (currentRequest && !refresh) return currentRequest;
      if (!refresh && state.positionsLoaded) return state.positions;

      let request!: Promise<Positions>;
      request = (async (): Promise<Positions> => {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getPositions();
        const positions = new Positions(
          response.data.map(
            (position: Partial<PositionListItemResponse>) =>
              new Position(position),
          ),
        );
        if (positionsRequests.get(state) === request)
          commit("setPositions", positions);
        return positions;
      })();
      positionsRequests.set(state, request);
      try {
        return await request;
      } catch (error) {
        if (refresh && positionsRequests.get(state) === request)
          commit("invalidatePositions");
        throw error;
      } finally {
        if (positionsRequests.get(state) === request)
          positionsRequests.delete(state);
      }
    },
    async getLivePositions() {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getLivePositions();
      return (
        // No cache for live positions
        new LivePositions(
          response.data.map(
            (position: Partial<LivePositionResponse>) =>
              new LivePosition(position),
          ),
        )
      );
    },
    async getPosition({ commit }, positionId: number) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getPosition(positionId);
      const position = new Position(response.data);
      commit("setPosition", position);
      return position;
    },
    async postPosition({ commit }, positionRequest: PositionRequest) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postPosition(positionRequest);
      const position = new Position(response.data);
      commit("setPosition", position);
      return position;
    },
    patchPosition(
      { commit },
      {
        includeMissingJobDetailsDefaults = false,
        positionId,
        positionRequest,
      }: {
        includeMissingJobDetailsDefaults?: boolean;
        positionId: number;
        positionRequest: PositionPatchRequest;
      },
    ) {
      const previous = patchPositionChains.get(positionId) ?? Promise.resolve();
      const chained = previous
        // An earlier failed PATCH must not block a later one for the same position.
        .catch(() => undefined)
        .then(async () => {
          const api: DefaultApi = await this.dispatch(
            "getAuthorizedApi",
            null,
            {
              root: true,
            },
          );
          let effectiveRequest = positionRequest;
          if (includeMissingJobDetailsDefaults) {
            const latestResponse = await api.getPosition(positionId);
            const latestPosition = new Position(latestResponse.data);
            effectiveRequest = {
              ...buildMissingJobDetailsDefaultsPatch(latestPosition),
              ...positionRequest,
            };
            if (Object.keys(effectiveRequest).length === 0) {
              commit("setPosition", latestPosition);
              return latestPosition;
            }
          }
          const response = await api.patchPosition(
            positionId,
            effectiveRequest,
          );
          const position = new Position(response.data);
          commit("setPosition", position);
          return position;
        });
      patchPositionChains.set(positionId, chained);
      // Drop the entry once settled, but only if a newer call hasn't replaced it already —
      // otherwise the map would grow forever, one stale entry per position id.
      void chained
        .catch(() => undefined)
        .finally(() => {
          if (patchPositionChains.get(positionId) === chained) {
            patchPositionChains.delete(positionId);
          }
        });
      return chained;
    },
    /**
     * Resolves once every patchPosition already enqueued for this id has landed in the store.
     * Publish uses this before rechecking completion so it never launches from stale store state.
     * Rejects if the last PATCH failed; this never poisons a later patchPosition call, which
     * swallows the previous entry's rejection before chaining its own PATCH.
     */
    async awaitPendingPatch(_context, positionId: number): Promise<void> {
      await (patchPositionChains.get(positionId) ?? Promise.resolve());
    },
    async postPositionDuplicate(
      { commit },
      {
        positionId,
        komboJobId,
      }: {
        positionId: number;
        komboJobId: string;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postPositionDuplicate(positionId, {
        kombo_job_id: komboJobId,
      });
      const position = new Position(response.data);
      commit("setPosition", position);
      return position;
    },
    async postPositionCall(
      _,
      {
        positionId,
        callTestRequest,
      }: {
        positionId: number;
        callTestRequest: CallTestRequest;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCallTest(positionId, callTestRequest);
      return response;
    },
    async postCallSimulation(
      _,
      payload: {
        positionId: number;
        reportRecipients: SimulationReportRecipients;
      },
    ) {
      const { positionId, reportRecipients } = payload;
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCallSimulation(positionId, {
        report_recipients: reportRecipients,
      });
      return new CallSimulationJob(response.data);
    },
    async getLatestCallSimulation(_, positionId: number) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getLatestCallSimulation(positionId);
      // 204 No Content → no job yet for this position
      if (response.status === 204) {
        return null;
      }
      return new CallSimulationJob(response.data);
    },
    async postCallSimulationCoverageRewrite(
      _,
      payload: {
        positionId: number;
        jobId: number;
        settingKey: string;
        comment: string;
      },
    ) {
      const { positionId, jobId, settingKey, comment } = payload;
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCallSimulationCoverageRewrite(
        positionId,
        jobId,
        settingKey,
        {
          comment,
        },
      );
      return response.data;
    },
    async generateQualificationQuestion(
      _,
      {
        positionId,
        request,
      }: {
        positionId?: number | null;
        request: PositionQualificationQuestionGenerationRequest;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const call =
        positionId != null
          ? api.postPositionGenerateQualificationQuestion(positionId, request)
          : api.postGenerateQualificationQuestion(request);
      const response = await call;
      return response.data;
    },
    async getPositionMetrics(
      _,
      {
        positionId,
        request,
      }: {
        positionId: number;
        request: PositionConversationFlowMetricsRequest;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postPositionMetrics(positionId, request);
      return response.data;
    },
    async getSyncPositionStatus(
      { state, commit },
      { useCache = false }: { useCache?: boolean } = {},
    ) {
      if (
        useCache &&
        state.positionSyncStatus &&
        state.positionSyncStatusLoaded
      )
        return state.positionSyncStatus;
      const pending = pendingPositionSyncStatus.get(state);
      if (pending) return pending;

      const request = Promise.resolve().then(async () => {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getSyncPositionStatus();
        const status = new PositionSyncStatus(response.data);
        if (pendingPositionSyncStatus.get(state) === request)
          commit("setPositionSyncStatus", status);
        return status;
      });
      pendingPositionSyncStatus.set(state, request);
      try {
        return await request;
      } finally {
        if (pendingPositionSyncStatus.get(state) === request)
          pendingPositionSyncStatus.delete(state);
      }
    },
    async postPositionSync({ commit }) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      await api.postPositionSync();
      commit("invalidatePositionSyncStatus");
      return;
    },
    async getIntegrationSyncStatuses(): Promise<
      IntegrationSyncStatusResponse[]
    > {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      return (await api.getIntegrationSyncStatuses()).data;
    },
    async refreshAts({ state, commit, dispatch }): Promise<number> {
      if (atsRefreshPromise) return atsRefreshPromise;

      const refresh = async (): Promise<number> => {
        let positions: Positions;
        try {
          positions = state.positions ?? (await dispatch("getPositions"));
        } catch (error) {
          console.error("Could not load positions for ATS refresh:", error);
          commit("setAtsSyncFailures", 1);
          return 1;
        }
        const work = [
          { key: "position", post: () => dispatch("postPositionSync") },
          ...positions
            .filter(
              (position: Position) =>
                position.kombo_job_id != null &&
                position.published === true &&
                position.archived !== true,
            )
            .flatMap((position: Position) => [
              {
                key: `resume:${position.id}`,
                post: () =>
                  dispatch("resume/postResumeSync", position.id, {
                    root: true,
                  }),
              },
              {
                key: `candidate:${position.id}`,
                post: () =>
                  dispatch("candidate/postCandidateSync", position.id, {
                    root: true,
                  }),
              },
            ]),
        ];
        let completed = 0;
        let failures = 0;
        let positionSucceeded = false;
        commit("setAtsSyncFailures", 0);
        commit("setAtsSyncing", { isSyncing: true, progress: 0 });

        const statusKey = (status: IntegrationSyncStatusResponse): string =>
          status.position_id === null
            ? "position"
            : `${status.sync_type}:${status.position_id}`;
        const pending = new Map<
          string,
          { previous: IntegrationSyncStatusResponse | null; postedAt: number }
        >();
        const complete = (key: string, succeeded: boolean): void => {
          pending.delete(key);
          completed += 1;
          if (!succeeded) failures += 1;
          if (key === "position") positionSucceeded = succeeded;
          commit("setAtsSyncing", {
            isSyncing: true,
            progress: Math.floor((completed / work.length) * 100),
          });
        };

        try {
          const baselineRows = (await dispatch(
            "getIntegrationSyncStatuses",
          )) as IntegrationSyncStatusResponse[];
          const baseline = new Map(
            baselineRows.map((status) => [statusKey(status), status]),
          );
          let postingDone = false;
          let statusUnavailable = false;
          const monitor = async (): Promise<void> => {
            let readFailures = 0;
            while (!postingDone || pending.size > 0) {
              await new Promise<void>((resolve) => setTimeout(resolve, 5000));
              if (statusUnavailable) continue;
              let rows: IntegrationSyncStatusResponse[];
              try {
                rows = await dispatch("getIntegrationSyncStatuses");
                readFailures = 0;
              } catch (error) {
                console.error("Could not read ATS sync statuses:", error);
                readFailures += 1;
                if (readFailures >= 3) {
                  statusUnavailable = true;
                  for (const key of pending.keys()) complete(key, false);
                }
                continue;
              }
              const current = new Map(
                rows.map((status) => [statusKey(status), status]),
              );
              for (const [key, task] of pending) {
                const status = current.get(key);
                const claimed =
                  status !== undefined &&
                  (task.previous?.status === "waiting" ||
                    task.previous?.sync_result_id !== status.sync_result_id);
                if (claimed && status.status !== "waiting") {
                  complete(key, status.status === "succeeded");
                } else if (
                  Date.now() - task.postedAt >
                  (claimed ? 30 * 60 * 1000 : 60 * 1000)
                ) {
                  complete(key, false);
                }
              }
            }
          };
          const monitoring = monitor();
          let next = 0;
          const worker = async (): Promise<void> => {
            while (next < work.length) {
              const task = work[next++];
              if (statusUnavailable) {
                complete(task.key, false);
                continue;
              }
              try {
                await task.post();
                if (statusUnavailable) complete(task.key, false);
                else
                  pending.set(task.key, {
                    previous: baseline.get(task.key) ?? null,
                    postedAt: Date.now(),
                  });
              } catch (error) {
                console.error("ATS sync failed to start:", error);
                complete(task.key, false);
              }
            }
          };
          await Promise.all(
            Array.from({ length: Math.min(6, work.length) }, () => worker()),
          );
          postingDone = true;
          await monitoring;
          if (positionSucceeded) {
            await Promise.all([
              dispatch("getSyncPositionStatus"),
              dispatch("getPositions", { refresh: true }),
              dispatch("jobState/getJobStates", null, { root: true }),
            ]);
          }
        } catch (error) {
          console.error("ATS refresh failed:", error);
          failures += 1;
        } finally {
          commit("setAtsSyncFailures", failures);
          commit("setAtsSyncing", { isSyncing: false, progress: 0 });
        }
        return failures;
      };

      atsRefreshPromise = refresh();
      try {
        return await atsRefreshPromise;
      } finally {
        atsRefreshPromise = null;
      }
    },
  },
  mutations: {
    setPosition(state, position: Position) {
      if (state.positions === undefined)
        state.positions = new Positions([position]);
      else state.positions = state.positions.update(position);
    },
    setPositions(state, positions: Positions) {
      state.positions = positions;
      state.positionsLoaded = true;
    },
    invalidatePositions(state) {
      state.positionsLoaded = false;
      positionsRequests.delete(state);
    },
    setPositionSyncStatus(state, positionSyncStatus: PositionSyncStatus) {
      state.positionSyncStatus = positionSyncStatus;
      state.positionSyncStatusLoaded = true;
    },
    invalidatePositionSyncStatus(state) {
      state.positionSyncStatusLoaded = false;
      pendingPositionSyncStatus.delete(state);
    },
    setAtsSyncing(
      state,
      payload: {
        isSyncing: boolean;
        progress: number;
      },
    ) {
      state.atsSyncing = payload.isSyncing;
      // Always reset progress to 0 when not syncing, as a safety guard
      // against stale progress values remaining visible after sync completes.
      state.atsSyncProgress = payload.isSyncing ? payload.progress : 0;
    },
    setAtsRefreshRequested(state, requested: boolean) {
      state.atsRefreshRequested = requested;
    },
    setAtsSyncFailures(state, failures: number) {
      state.atsSyncFailures = failures;
    },
    setAutoInviteState(
      state,
      {
        positionId,
        configured,
        paused,
      }: {
        positionId: number;
        configured?: boolean;
        paused?: boolean;
      },
    ) {
      const positions = state.positions;
      const position = positions?.get(positionId);
      if (positions === undefined || position === undefined) return;
      state.positions = positions.update(
        new Position({
          ...position,
          automated_invite: {
            configured:
              configured ?? position.automated_invite?.configured ?? false,
            paused: paused ?? position.automated_invite?.paused ?? false,
          },
        }),
      );
    },
  },
};
